/**
 * Appointment service for managing therapy appointments
 */
import StorageService from './StorageService';
import Appointment, { APPOINTMENT_STATUS } from '../models/Appointment';
import practitionerService from './PractitionerService';
import customerService from './CustomerService';
import treatmentService from './TreatmentService';

class AppointmentService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('appointments');
  }

  /**
   * Get all appointments
   * @returns {Array} All appointments
   */
  getAllAppointments() {
    return this.storageService.getAllAsArray().map(appointment =>
      Appointment.fromJSON(appointment)
    );
  }

  /**
   * Get appointment by ID
   * @param {string} id - Appointment ID
   * @returns {Appointment|null} Appointment or null if not found
   */
  getAppointmentById(id) {
    const appointmentData = this.storageService.getById(id);
    if (!appointmentData) return null;

    return Appointment.fromJSON(appointmentData);
  }

  /**
 * Create appointment
 * @param {Object} appointmentData - Appointment data
 * @returns {Object} Creation result with appointment and conflicts info
 */
  createAppointment(appointmentData) {
    // Map appointmentTime to startTime and calculate endTime
    const processedData = { ...appointmentData };

    if (appointmentData.appointmentTime) {
      processedData.startTime = appointmentData.appointmentTime;

      // Calculate end time based on duration
      if (appointmentData.duration) {
        const startTime = new Date(appointmentData.appointmentTime);
        const endTime = new Date(startTime.getTime() + (appointmentData.duration * 60000)); // duration in minutes
        processedData.endTime = endTime.toISOString();
      }

      // Remove appointmentTime as it's now mapped to startTime
      delete processedData.appointmentTime;
    }

    const appointment = new Appointment(processedData);

    // Check for conflicts with other appointments
    const conflicts = this.checkForConflicts(appointment);

    // Save the appointment
    this.storageService.save(appointment.id, appointment.toJSON());

    return {
      appointment,
      conflicts: conflicts.length > 0,
      conflictDetails: conflicts
    };
  }

  /**
 * Update appointment
 * @param {string} id - Appointment ID
 * @param {Object} appointmentData - Appointment data to update
 * @returns {Object} Update result with appointment and conflicts info
 */
  updateAppointment(id, appointmentData) {
    const existingAppointmentData = this.storageService.getById(id);
    if (!existingAppointmentData) {
      return {
        success: false,
        message: '预约不存在'
      };
    }

    // Map appointmentTime to startTime and calculate endTime
    const processedData = { ...appointmentData };

    if (appointmentData.appointmentTime) {
      processedData.startTime = appointmentData.appointmentTime;

      // Calculate end time based on duration
      if (appointmentData.duration) {
        const startTime = new Date(appointmentData.appointmentTime);
        const endTime = new Date(startTime.getTime() + (appointmentData.duration * 60000)); // duration in minutes
        processedData.endTime = endTime.toISOString();
      }

      // Remove appointmentTime as it's now mapped to startTime
      delete processedData.appointmentTime;
    }

    const appointment = Appointment.fromJSON(existingAppointmentData);
    appointment.update(processedData);

    // Check for conflicts with other appointments if time changed
    let conflicts = [];
    if (processedData.startTime || processedData.endTime || processedData.practitionerId) {
      conflicts = this.checkForConflicts(appointment);
    }

    // Save the appointment
    this.storageService.save(appointment.id, appointment.toJSON());

    return {
      success: true,
      appointment,
      conflicts: conflicts.length > 0,
      conflictDetails: conflicts
    };
  }

  /**
   * Delete appointment
   * @param {string} id - Appointment ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteAppointment(id) {
    return this.storageService.delete(id);
  }

  /**
   * Change appointment status
   * @param {string} id - Appointment ID
   * @param {string} status - New status
   * @param {string} [note=''] - Note about status change
   * @returns {Appointment|null} Updated appointment or null if not found
   */
  changeAppointmentStatus(id, status, note = '') {
    const appointment = this.getAppointmentById(id);
    if (!appointment) return null;

    appointment.setStatus(status, note);
    this.storageService.save(appointment.id, appointment.toJSON());

    return appointment;
  }

  /**
   * Update appointment status (alias for changeAppointmentStatus)
   * @param {string} id - Appointment ID
   * @param {string} status - New status
   * @param {string} [note=''] - Note about status change
   * @returns {Appointment|null} Updated appointment or null if not found
   */
  updateAppointmentStatus(id, status, note = '') {
    return this.changeAppointmentStatus(id, status, note);
  }

  /**
   * Check for conflicts with other appointments
   * @param {Appointment} appointment - Appointment to check
   * @returns {Array} Conflicting appointments
   * @private
   */
  checkForConflicts(appointment) {
    // Get all appointments with same practitioner
    const potentialConflicts = this.getAppointmentsByPractitioner(appointment.practitionerId)
      .filter(a => a.id !== appointment.id); // Exclude the appointment itself

    // Check for time conflicts
    return potentialConflicts.filter(existingAppointment =>
      appointment.conflictsWith(existingAppointment)
    );
  }

  /**
   * Get appointments by date range
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @returns {Array} Appointments in date range
   */
  getAppointmentsByDateRange(startDate, endDate) {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);

    start.setHours(0, 0, 0, 0); // Beginning of start day
    end.setHours(23, 59, 59, 999); // End of end day

    return this.storageService.find(appointment => {
      const appointmentStart = new Date(appointment.startTime);
      return appointmentStart >= start && appointmentStart <= end;
    }).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments for today
   * @returns {Array} Today's appointments
   */
  getTodaysAppointments() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);

    return this.getAppointmentsByDateRange(today, tomorrow);
  }

  /**
   * Get appointments by customer
   * @param {string} customerId - Customer ID
   * @returns {Array} Customer's appointments
   */
  getAppointmentsByCustomer(customerId) {
    return this.storageService.find(appointment =>
      appointment.customerId === customerId
    ).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments by practitioner
   * @param {string} practitionerId - Practitioner ID
   * @returns {Array} Practitioner's appointments
   */
  getAppointmentsByPractitioner(practitionerId) {
    return this.storageService.find(appointment =>
      appointment.practitionerId === practitionerId
    ).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments by venue
   * @param {string} venueId - Venue ID
   * @returns {Array} Appointments in venue
   */
  getAppointmentsByVenue(venueId) {
    return this.storageService.find(appointment =>
      appointment.venueId === venueId
    ).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments by status
   * @param {string} status - Appointment status
   * @returns {Array} Appointments with status
   */
  getAppointmentsByStatus(status) {
    return this.storageService.find(appointment =>
      appointment.status === status
    ).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments by treatment
   * @param {string} treatmentId - Treatment ID
   * @returns {Array} Appointments for treatment
   */
  getAppointmentsByTreatment(treatmentId) {
    return this.storageService.find(appointment =>
      appointment.treatmentId === treatmentId
    ).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Search appointments
   * @param {string} query - Search query
   * @returns {Array} Matching appointments
   */
  searchAppointments(query) {
    if (!query) return this.getAllAppointments();

    const lowercasedQuery = query.toLowerCase();

    // First get customer IDs matching the query
    const customersService = customerService;
    const matchingCustomers = customersService.searchCustomers(query).map(c => c.id);

    return this.storageService.find(appointment => {
      // Match by customer ID or notes
      return (
        matchingCustomers.includes(appointment.customerId) ||
        appointment.notes?.toLowerCase().includes(lowercasedQuery)
      );
    }).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Mark appointment as paid
   * @param {string} id - Appointment ID
   * @param {string} [method='cash'] - Payment method
   * @param {string} [note=''] - Payment note
   * @returns {Appointment|null} Updated appointment or null if not found
   */
  markAppointmentAsPaid(id, method = 'cash', note = '') {
    const appointment = this.getAppointmentById(id);
    if (!appointment) return null;

    appointment.markAsPaid(method, note);
    this.storageService.save(appointment.id, appointment.toJSON());

    return appointment;
  }

  /**
   * Get upcoming appointments
   * @param {number} [days=7] - Number of days to look ahead
   * @returns {Array} Upcoming appointments
   */
  getUpcomingAppointments(days = 7) {
    const now = new Date();
    const future = new Date(now);
    future.setDate(future.getDate() + days);

    return this.storageService.find(appointment => {
      if (appointment.status === APPOINTMENT_STATUS.CANCELLED) return false;
      if (appointment.status === APPOINTMENT_STATUS.COMPLETED) return false;

      const appointmentTime = new Date(appointment.startTime);
      return appointmentTime >= now && appointmentTime <= future;
    }).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Get appointments that need reminders sent
   * @param {number} [hoursAhead=24] - Hours ahead to check
   * @returns {Array} Appointments needing reminders
   */
  getAppointmentsNeedingReminders(hoursAhead = 24) {
    const now = new Date();
    const future = new Date(now);
    future.setHours(now.getHours() + hoursAhead);

    return this.storageService.find(appointment => {
      if (appointment.status !== APPOINTMENT_STATUS.CONFIRMED) return false;
      if (appointment.reminderSent) return false;

      const appointmentTime = new Date(appointment.startTime);
      return appointmentTime >= now && appointmentTime <= future;
    }).map(appointment => Appointment.fromJSON(appointment));
  }

  /**
   * Mark appointment reminder as sent
   * @param {string} id - Appointment ID
   * @returns {Appointment|null} Updated appointment or null if not found
   */
  markReminderSent(id) {
    const appointment = this.getAppointmentById(id);
    if (!appointment) return null;

    appointment.update({ reminderSent: true });
    this.storageService.save(appointment.id, appointment.toJSON());

    return appointment;
  }

  /**
   * Get appointment with customer and practitioner details
   * @param {string} id - Appointment ID
   * @returns {Object|null} Appointment with related details
   */
  getAppointmentWithDetails(id) {
    const appointment = this.getAppointmentById(id);
    if (!appointment) return null;

    // Get related data
    const customer = customerService.getCustomerById(appointment.customerId);
    const practitioner = practitionerService.getPractitionerById(appointment.practitionerId);
    const treatment = treatmentService.getTreatmentById(appointment.treatmentId);

    return {
      ...appointment,
      customerDetails: customer ? {
        name: customer.name,
        phone: customer.phone,
        email: customer.email,
        id: customer.id
      } : null,
      practitionerDetails: practitioner ? {
        name: practitioner.name,
        id: practitioner.id
      } : null,
      treatmentDetails: treatment ? {
        name: treatment.name,
        duration: treatment.duration,
        id: treatment.id
      } : null
    };
  }

  /**
   * Get total number of appointments
   * @returns {number} Appointment count
   */
  getAppointmentCount() {
    return this.storageService.count();
  }

  /**
   * Get appointment metrics by status
   * @returns {Object} Metrics by status
   */
  getAppointmentMetricsByStatus() {
    const appointments = this.getAllAppointments();
    const metrics = {};

    // Initialize counters for all statuses
    Object.values(APPOINTMENT_STATUS).forEach(status => {
      metrics[status] = 0;
    });

    // Count appointments by status
    appointments.forEach(appointment => {
      if (metrics[appointment.status] !== undefined) {
        metrics[appointment.status]++;
      }
    });

    return metrics;
  }

  /**
   * Import appointments from JSON
   * @param {string} jsonData - JSON string of appointment data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importAppointments(jsonData, merge = false) {
    return this.storageService.importData(jsonData, merge);
  }

  /**
   * Export appointments as JSON
   * @returns {string} JSON string of appointment data
   */
  exportAppointments() {
    return this.storageService.exportData();
  }
}

// Create singleton instance
const appointmentService = new AppointmentService();

export default appointmentService;